import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useData } from '../context/DataContext';
import { AudioWaveform } from '../components/AudioWaveform';
import { LyricsDisplay } from '../components/LyricsDisplay';
import { Song } from '../services/SongService';
import { AlertCircle, X, Settings } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface LoadingState {
  audio: boolean;
  lyrics: boolean;
}

export const KODStudio: React.FC = () => {
  const { songId } = useParams<{ songId: string }>();
  const { songService } = useData();
  const navigate = useNavigate();
  
  const [song, setSong] = useState<Song | null>(null);
  const [audioUrl, setAudioUrl] = useState<string>('');
  const [lyrics, setLyrics] = useState<string>('');
  const [duration, setDuration] = useState<number>(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [showExitModal, setShowExitModal] = useState(false);
  const [errors, setErrors] = useState<{ audio?: string; lyrics?: string }>({});
  const [loading, setLoading] = useState<LoadingState>({ audio: true, lyrics: true });
  const [showSettings, setShowSettings] = useState(false);
  const [pitch, setPitch] = useState(0);
  const [tempo, setTempo] = useState(1);
  const [volume, setVolume] = useState(1);

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    if (songId) {
      const loadSong = async () => {
        try {
          // Reset states on new load attempt
          setErrors({});
          setLoading({ audio: true, lyrics: true });

          const songData = await songService.getSong(songId);
          if (!songData) {
            throw new Error('Song not found');
          }
          setSong(songData);

          // Load audio and lyrics separately to handle individual errors
          const loadAudio = async () => {
            try {
              // Use practice track (full mix with vocals) for practice mode
              const audioUrlData = await songService.getFullMixUrl(songId);
              setAudioUrl(audioUrlData);
            } catch (error) {
              setErrors(prev => ({ ...prev, audio: 'Unable to load practice track. Please try again later.' }));
            } finally {
              setLoading(prev => ({ ...prev, audio: false }));
            }
          };

          const loadLyrics = async () => {
            try {
              const lyricsData = await songService.getLyrics(songId);
              setLyrics(lyricsData);
            } catch (error) {
              setErrors(prev => ({ ...prev, lyrics: 'Unable to load lyrics. Please try again later.' }));
            } finally {
              setLoading(prev => ({ ...prev, lyrics: false }));
            }
          };

          // Load both in parallel
          await Promise.all([loadAudio(), loadLyrics()]);

        } catch (error) {
          console.error('Error loading song:', error);
          setErrors({ audio: 'Unable to load the song. Please check if the song exists and try again.' });
          setLoading({ audio: false, lyrics: false });
        }
      };

      loadSong();
    }
  }, [songId]);

  if (Object.keys(errors).length > 0 && !loading.audio && !loading.lyrics) {
    return (
      <div className="flex flex-col items-center justify-center h-screen p-4">
        <div className="flex items-center gap-2 text-red-500 mb-4">
          <AlertCircle className="w-6 h-6" />
          <h2 className="text-xl font-semibold">Error</h2>
        </div>
        <div className="text-center text-white/70">
          {errors.audio && <p className="mb-2">{errors.audio}</p>}
          {errors.lyrics && <p>{errors.lyrics}</p>}
        </div>
      </div>
    );
  }

  if (!song) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-kod-accent"></div>
      </div>
    );
  }

  return (
    <div className="h-screen relative">
      <div className="container mx-auto px-6 h-full pt-5">
      {/* Exit and Settings Buttons */}
      <div className="absolute top-6 right-6 z-10 flex flex-col gap-2">
        <button
          onClick={() => setShowExitModal(true)}
          className="p-2 rounded-full bg-kod-purple-700 hover:bg-kod-purple-600 transition-colors"
        >
          <X size={24} />
        </button>
        <button
          onClick={() => setShowSettings(true)}
          className="p-2 rounded-full bg-kod-purple-700 hover:bg-kod-purple-600 transition-colors"
        >
          <Settings size={24} />
        </button>
      </div>

      {/* Main Content */}
      <div className="space-y-6">
        {/* Display lyrics */}
        {!lyrics || loading.lyrics ? (
          <div className="bg-kod-purple-800 rounded-lg p-6 border border-kod-purple-600/40">
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-kod-accent"></div>
            </div>
          </div>
        ) : !errors.lyrics ? (
          <LyricsDisplay 
            lrcContent={lyrics}
            currentTime={currentTime}
          />
        ) : (
          <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4 text-yellow-400">
            {errors.lyrics}
          </div>
        )}
        
        {/* Audio waveform and song info */}
        <div className="absolute bottom-0 left-0 right-0 bg-kod-purple-900/95 backdrop-blur-sm">
          <div className="container mx-auto px-6 py-4">
            {!audioUrl || loading.audio ? (
              <div className="bg-kod-purple-800 rounded-lg p-6">
                <div className="flex justify-center items-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-kod-accent"></div>
                </div>
              </div>
            ) : !errors.audio ? (
              <AudioWaveform 
                url={audioUrl} 
                onTimeUpdate={setCurrentTime}
                onDurationChange={setDuration}
                pitch={pitch}
                tempo={tempo}
                volume={volume}
              />
            ) : (
              <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 text-red-400">
                {errors.audio}
              </div>
            )}
            <div className="flex justify-end mt-2 text-white/70 font-medium">
              {formatTime(currentTime)} / {formatTime(duration)}
            </div>
            <div className="flex items-center justify-between mt-2">
              <div className="flex items-center gap-2">
                <h1 className="text-xl font-bold">{song.title}</h1>
                <span className="text-white/50">•</span>
                <p className="text-white/70">{song.artist}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>
      {/* Exit Confirmation Modal */}
      {showExitModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-kod-purple-800 p-6 rounded-lg max-w-md w-full mx-4 border border-kod-purple-600">
            <h3 className="text-xl font-bold mb-4">Exit Recording Session?</h3>
            <p className="text-white/70 mb-6">
              Are you sure you want to exit? Any unsaved progress will be lost.
            </p>
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => setShowExitModal(false)}
                className="px-4 py-2 rounded-md bg-kod-purple-700 hover:bg-kod-purple-600 transition-colors"
              >
                Stay
              </button>
              <button
                onClick={() => navigate('/dashboard')}
                className="px-4 py-2 rounded-md bg-red-500 hover:bg-red-600 transition-colors"
              >
                Exit
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Settings Modal */}
      {showSettings && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-kod-purple-800 p-6 rounded-lg max-w-md w-full mx-4 border border-kod-purple-600">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold">Playback Settings</h3>
              <button
                onClick={() => setShowSettings(false)}
                className="p-2 hover:bg-kod-purple-700 rounded-full"
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="space-y-6">
              {/* Pitch Control */}
              <div>
                <div className="flex justify-between mb-2">
                  <label className="text-sm font-medium">Pitch</label>
                  <span className="text-sm text-white/70">{pitch > 0 ? `+${pitch}` : pitch} semitones</span>
                </div>
                <input
                  type="range"
                  min="-12"
                  max="12"
                  step="1"
                  value={pitch}
                  onChange={(e) => setPitch(Number(e.target.value))}
                  className="w-full h-2 bg-kod-purple-700 rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-kod-accent"
                />
              </div>
              
              {/* Tempo Control */}
              <div>
                <div className="flex justify-between mb-2">
                  <label className="text-sm font-medium">Tempo</label>
                  <span className="text-sm text-white/70">{Math.round(tempo * 100)}%</span>
                </div>
                <input
                  type="range"
                  min="0.5"
                  max="2"
                  step="0.05"
                  value={tempo}
                  onChange={(e) => setTempo(Number(e.target.value))}
                  className="w-full h-2 bg-kod-purple-700 rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-kod-accent"
                />
              </div>
              
              {/* Volume Control */}
              <div>
                <div className="flex justify-between mb-2">
                  <label className="text-sm font-medium">Volume</label>
                  <span className="text-sm text-white/70">{Math.round(volume * 100)}%</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.05"
                  value={volume}
                  onChange={(e) => setVolume(Number(e.target.value))}
                  className="w-full h-2 bg-kod-purple-700 rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-kod-accent"
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};